/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  styled,
  Typography,
  useTheme
} from '@mui/material';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppSelector } from 'src/app/hooks';
import { BILLING_CHOOSE_YOUR_PLAN } from 'src/route';
import useBillingSystem from 'src/services/useBillingSystem';
import separateNumberFormat from 'src/utils/separateNumberFormat';
import CloseIcon from '@mui/icons-material/Close';
import PackagePlanSummary from 'src/components/PackagePlanSummary';
import { INIT_PERIOD_OPTIONS, IPeriodOption } from '../ChooseYourPlan';
import {
  BillableMetric,
  PeriodSubscription,
  PlanSimulationResponse,
  PlanSimulationV2Params,
  UserPackagePlan,
  UserPackagePlanName
} from 'src/models/register';
import { useQuery } from '@apollo/client';
import {
  GET_BILLABLE_METRICS,
  GET_PLAN_SIMULATION_V2
} from 'src/services/graphql/register/query';
import { themeColors } from 'src/theme/schemes/PureLightTheme';
import { getPromoCode, promoCodesByPeriod } from 'src/utils/promoCodeUtils';

const Container = styled(Box)(
  ({ theme }) => `
    box-shadow: rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px;
    border-radius: ${theme.spacing(1)};
    padding: ${theme.spacing(2)};
    width: 100%;
    background: ${theme.palette.common.white}
      `
);

const Item = styled(Box)(
  ({ theme }) => `
      border: 1px solid rgba(11, 133, 174, 0.14);
      border-radius: ${theme.spacing(1.5)};
      padding: ${theme.spacing(3)};
      width: 100%;
        `
);

const initCountryId = '9cd88e16-510e-49f0-9ce8-f4d27f0b85bb';

interface ModalPlanSummaryProps {
  open: boolean;
  handleClose: () => void;
  // onDone: () => void;
}

interface CustomBackground {
  customBackground: string;
}

const LiteTypography = styled(Typography)<CustomBackground>(
  ({ customBackground }) => `
  background: ${customBackground};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-transform: capitalize;
  `
);

export const getPackageColor = (planName: string) => {
  switch (planName.toLowerCase()) {
    case 'starter':
      return 'linear-gradient(135deg, #FCCF31 0%, #F55555 100%)';
    case 'lite':
      return 'linear-gradient(92.9deg, #47A3E6 -4.64%, #54CEE9 101.13%)';
    case 'advanced':
      return 'linear-gradient(135deg, #43CBFF 0%, #9708CC 100%)';
    default:
      return themeColors.blue;
  }
};

const ModalPlanSummary = ({
  open,
  handleClose
}: // onDone
ModalPlanSummaryProps) => {
  const theme = useTheme();
  const projectId = useAppSelector((state) => state.users.project.id);
  const { useGetUserSubscription, createUpgradePlanPaymentLink } =
    useBillingSystem();

  const countryId = useAppSelector(
    (state) => state.storeBilling.currentPlan.data.countryId
  );

  const { data: subscriptionData, loading: isLoading } = useGetUserSubscription(
    {
      variables: { projectId: projectId }
      // onCompleted: (data) => {
      //   const nextPlan = data.billing_getUserSubscription.nextUserCycle;
      //   const promoCodes = getPromoCode(
      //     nextPlan.packagePlan.name ?? '',
      //     activePeriod.value,
      //     isFirstPayment
      //   );
      //   setPromoCodes(promoCodes);
      // }
    }
  );

  const { email } = useAppSelector((state) => state.users.userProfile.data);

  const {
    data: billableMetrics,
    loading: billableMetricsLoading,
    error: billableMetricsError
  } = useQuery<{ billing_getBillableMetrics: BillableMetric[] }>(
    GET_BILLABLE_METRICS,
    {
      fetchPolicy: 'cache-first',
      skip: !open
    }
  );

  const nextPlan = subscriptionData?.billing_getUserSubscription?.nextUserCycle;
  const currentCycleNumber =
    subscriptionData?.billing_getUserSubscription?.currentUserCycle
      ?.userSubscriptionCycle?.cycleNumber;
  const initialReferalCode =
    currentCycleNumber <= 1
      ? subscriptionData?.billing_getUserSubscription?.referalCode
      : '';
  const isFirstPayment =
    subscriptionData?.billing_getUserSubscription?.isFirstPayment;

  const activePeriod = useMemo(
    () =>
      INIT_PERIOD_OPTIONS.find(
        (period) => period.value === nextPlan?.userSubscriptionCycle?.period
      ),
    [nextPlan?.userSubscriptionCycle?.period]
  );

  const [period, setPeriod] = useState<IPeriodOption>(activePeriod);

  const initialPromoCodes = getPromoCode(
    nextPlan?.packagePlan?.name ?? '',
    activePeriod.value,
    isFirstPayment
  );

  const [promoCodes, setPromoCodes] = useState<string[]>(initialPromoCodes);
  const [referalCode, setReferalCode] = useState<string>(initialReferalCode);
  const renderDisplayName = (name: string) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const currentPackagePlan: UserPackagePlan = useMemo(() => {
    if (!subscriptionData) return null;
    const metrics =
      subscriptionData.billing_getUserSubscription.billableMetricPackage;

    const basicObjectQuota =
      metrics.find((metric) => metric.name === 'Social Profile')?.quota ?? 0;
    const advanceObjectQuota =
      metrics.find((metric) => metric.name === 'Keywords & Hashtags')?.quota ??
      0;
    const entityQuota =
      metrics.find((metric) => metric.name === 'Entity')?.quota ?? 0;
    const onlineMediaObjectQuota =
      metrics.find((metric) => metric.name === 'Clipping')?.quota ?? 0;

    return {
      id: subscriptionData.billing_getUserSubscription.nextUserCycle.packagePlan
        .id,
      displayName: renderDisplayName(
        subscriptionData.billing_getUserSubscription.nextUserCycle.packagePlan
          .displayName
      ),
      basicObjectQuota,
      advanceObjectQuota,
      entityQuota,
      onlineMediaObjectQuota,
      name: subscriptionData.billing_getUserSubscription.nextUserCycle
        .packagePlan.name as UserPackagePlanName
    };
  }, [subscriptionData]);

  const planItems = useMemo(() => {
    if (!billableMetrics || !currentPackagePlan) return [];
    return billableMetrics.billing_getBillableMetrics
      .map((metric) => {
        if (metric.code === 'SOCIAL_PROFILE')
          return {
            billableMetricId: metric.id,
            quota: currentPackagePlan.basicObjectQuota
          };
        if (metric.code === 'KEYWORD_HASHTAG')
          return {
            billableMetricId: metric.id,
            quota: currentPackagePlan.advanceObjectQuota
          };
        if (metric.code === 'CLIPPING')
          return {
            billableMetricId: metric.id,
            quota: currentPackagePlan.onlineMediaObjectQuota
          };
        if (metric.code === 'ENTITY')
          return {
            billableMetricId: metric.id,
            quota: currentPackagePlan.entityQuota
          };
        return null;
      })
      .filter((item) => item !== null);
  }, [billableMetrics, currentPackagePlan]);

  const isCustomPlan = currentPackagePlan.name === 'custom';

  const { data: planSimulationResult } = useQuery<
    {
      billing_getPlanSimulationV2: PlanSimulationResponse;
    },
    PlanSimulationV2Params
  >(GET_PLAN_SIMULATION_V2, {
    variables: {
      countryId: countryId || initCountryId,
      period: activePeriod.value,
      promoCodes: initialPromoCodes,
      planItems: isCustomPlan ? planItems : null,
      referalCode: initialReferalCode,
      email,
      packagePlanId: !isCustomPlan ? currentPackagePlan.id : null
    },
    skip: !open || !planItems.length || !activePeriod,

    // onCompleted: (data) => {
    //   if (
    //     data.billing_getPlanSimulationV2.isPromoValid === false &&
    //     data.billing_getPlanSimulationV2.promoCodes.length > 0
    //   ) {
    //     setIsPromoError(true);
    //     setPromoCodeErrorMessage(
    //       data.billing_getPlanSimulationV2.promoErrorMessage
    //     );
    //   } else {
    //     setIsPromoError(false);
    //     setPlanSimulation(data.billing_getPlanSimulationV2);
    //     onPromoUpdate?.(data.billing_getPlanSimulationV2);
    //   }
    // },
    fetchPolicy: 'network-only'
  });

  const handleClickCheckout = () => {
    const isCustomPlan =
      subscriptionData.billing_getUserSubscription.nextUserCycle.packagePlan
        .name === 'custom';

    const isReferalCodeValid =
      planSimulationResult?.billing_getPlanSimulationV2?.isReferralValid &&
      planSimulationResult?.billing_getPlanSimulationV2?.referralCode.length;

    const finalPayload = {
      periodSubscription: period.id as PeriodSubscription,
      planItems: isCustomPlan ? planItems : null,
      promoCodes: promoCodes.length > 0 ? promoCodes : undefined,
      userSubscriptionCycleId: nextPlan.userSubscriptionCycle.id,
      referalCode: isReferalCodeValid
        ? planSimulationResult?.billing_getPlanSimulationV2?.referralCode
        : undefined,
      packagePlanId: !isCustomPlan
        ? subscriptionData.billing_getUserSubscription.nextUserCycle.packagePlan
            .id
        : null
    };

    createUpgradePlanPaymentLink({
      variables: finalPayload,
      onCompleted: (data) => {
        window.location.href = data.billing_createPaymentLinkBilling.url;
      }
    });
  };

  return (
    <Dialog onClose={handleClose} open={open} fullWidth maxWidth={'sm'}>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{
          m: { xs: 0, md: theme.spacing(3, 3) }
        }}
      >
        <Stack gap={2} alignItems="center">
          {billableMetricsLoading ||
          !planSimulationResult?.billing_getPlanSimulationV2 ? (
            <CircularProgress />
          ) : (
            <>
              <PackagePlanSummary
                period={period}
                promoCodes={promoCodes}
                plan={currentPackagePlan}
                planItems={planItems}
                planSimulation={
                  planSimulationResult?.billing_getPlanSimulationV2
                }
                onPromoUpdate={(simulation) =>
                  setPromoCodes(simulation.promoCodes)
                }
                onReferalUpdate={(simulation) =>
                  setReferalCode(simulation.referralCode)
                }
                onPeriodUpdate={(period) => setPeriod(period)}
                referralCode={initialReferalCode ?? ''}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleClickCheckout}
                size="large"
                sx={{ px: 6, mt: 2, mx: 'auto' }}
              >
                Checkout
              </Button>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const CurrentPlan = () => {
  const theme = useTheme();
  const projectId = useAppSelector((state) => state.users.project.id);
  const navigate = useNavigate();
  const [openModalPlanSummary, setOpenModalPlanSummary] =
    useState<boolean>(false);

  const {
    useGetUserSubscription
    // postCreatePaymentLinkManually,
    // responseCreatePaymentLinkManually
  } = useBillingSystem();
  const { data: subscriptionData, loading: isLoading } = useGetUserSubscription(
    { variables: { projectId: projectId } }
  );

  const currentPlan =
    subscriptionData?.billing_getUserSubscription?.currentUserCycle;
  const nextPlan = subscriptionData?.billing_getUserSubscription?.nextUserCycle;

  const isNextPlanPaid = nextPlan?.userInvoice?.paymentStatus === 'paid';
  const isFreeTrial = currentPlan?.userSubscriptionCycle?.isFreeTrial;

  const getPeriod = (period: number) => {
    return INIT_PERIOD_OPTIONS.find((p) => p.value === period)?.period || '';
  };

  const loadingSection = (
    <Stack justifyContent="center" alignItems="center" height="450px">
      <CircularProgress size={70} />
    </Stack>
  );

  // const handleClickCreatePaymentLinkManually = async () => {
  //   if (!nextPlan) return;
  //   postCreatePaymentLinkManually({
  //     variables: { userSubscriptionCycleId: nextPlan.userSubscriptionCycle.id },
  //     onCompleted: (data) => {
  //       window.location.href = data.billing_createPaymentLinkManually.url;
  //     }
  //   });
  // };

  const renderDisplayName = (name: string) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <Container>
      {isLoading || !subscriptionData ? (
        loadingSection
      ) : (
        <>
          <Box sx={{ paddingBottom: theme.spacing(1.5) }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Current Plan
            </Typography>
          </Box>
          <Item>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <LiteTypography
                  variant="h3"
                  fontWeight={700}
                  gutterBottom
                  customBackground={getPackageColor(
                    isFreeTrial
                      ? 'Free Trial Plan'
                      : currentPlan.packagePlan.displayName
                  )}
                >
                  {isFreeTrial
                    ? 'Free Trial Plan'
                    : renderDisplayName(currentPlan.packagePlan.displayName)}
                </LiteTypography>
                <Stack direction="row" sx={{ margin: theme.spacing(1, 0) }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: theme.spacing(2.5),
                      lineHeight: '22px',
                      color: theme.palette.primary.main
                    }}
                  >
                    {`Rp${separateNumberFormat(
                      currentPlan.userInvoice.amount
                    )}`}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      lineHeight: '22px',
                      color: theme.palette.primary.main
                    }}
                  >
                    {`/${getPeriod(
                      currentPlan?.userSubscriptionCycle?.period
                    )}`}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      lineHeight: '22px',
                      color: '#9D9D9C',
                      marginLeft: theme.spacing(1),
                      textTransform: 'capitalize'
                    }}
                  >
                    {''}
                  </Typography>
                </Stack>
                <Stack direction="row" sx={{ margin: theme.spacing(1, 0) }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 400,
                      lineHeight: '22px',
                      color: theme.palette.success.main
                    }}
                  >
                    {currentPlan.userSubscriptionCycle.status[0].toUpperCase() +
                      currentPlan.userSubscriptionCycle.status.slice(1)}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 400,
                      lineHeight: '22px',
                      color: theme.palette.grey[500],
                      opacity: 0.9,
                      marginLeft: theme.spacing(1)
                    }}
                  >
                    {`End ${moment(currentPlan.userInvoice.periodEnd).from(
                      moment(new Date())
                    )}`}
                  </Typography>
                </Stack>
                <Stack direction="row" sx={{ margin: theme.spacing(1, 0) }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 400,
                      lineHeight: '22px',
                      color: theme.palette.grey[800],
                      opacity: 0.9
                    }}
                  >
                    {`Your next billing date is ${moment(
                      nextPlan.userInvoice.periodStart
                    ).format('ll')}`}
                  </Typography>
                </Stack>
                <Stack direction="row">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 400,
                      lineHeight: '22px',
                      color: theme.palette.grey[800],
                      opacity: 0.9
                    }}
                  >
                    {''}
                  </Typography>
                </Stack>
              </Box>
              <Box
                sx={{
                  padding: theme.spacing(2),
                  background: '#F4FCFF',
                  borderRadius: '6px'
                }}
              >
                {subscriptionData?.billing_getUserSubscription?.billableMetricPackage.map(
                  (metric) =>
                    metric.quota <= 0 ? null : (
                      <Stack
                        direction="row"
                        sx={{ margin: theme.spacing(1, 0) }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            lineHeight: '22px',
                            color: theme.palette.primary.dark
                          }}
                        >
                          {metric.quota}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 400,
                            lineHeight: '22px',
                            color: theme.palette.grey[500],
                            marginLeft: theme.spacing(1)
                          }}
                        >
                          {metric.name}
                        </Typography>
                      </Stack>
                    )
                )}
              </Box>
            </Stack>
          </Item>
          <Box sx={{ paddingBottom: theme.spacing(1.5), mt: theme.spacing(3) }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Next Plan
            </Typography>
          </Box>
          <Item>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <LiteTypography
                  variant="h3"
                  fontWeight={700}
                  gutterBottom
                  customBackground={getPackageColor(
                    nextPlan.packagePlan.displayName
                  )}
                >
                  {renderDisplayName(nextPlan.packagePlan.displayName)}
                </LiteTypography>
                <Stack direction="row" sx={{ margin: theme.spacing(1, 0) }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: theme.spacing(2.5),
                      lineHeight: '22px',
                      color: theme.palette.primary.main
                    }}
                  >
                    {`Rp${separateNumberFormat(nextPlan.userInvoice.amount)}`}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      lineHeight: '22px',
                      color: theme.palette.primary.main
                    }}
                  >
                    {`/${getPeriod(nextPlan?.userSubscriptionCycle?.period)}`}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      lineHeight: '22px',
                      color: '#9D9D9C',
                      marginLeft: theme.spacing(1),
                      textTransform: 'capitalize'
                    }}
                  >
                    {''}
                  </Typography>
                </Stack>
                <Stack direction="row" sx={{ margin: theme.spacing(1, 0) }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 400,
                      lineHeight: '22px',
                      color:
                        nextPlan.userInvoice.paymentStatus === 'paid'
                          ? theme.palette.success.main
                          : theme.palette.error.main
                    }}
                  >
                    {nextPlan.userInvoice.paymentStatus[0].toUpperCase() +
                      nextPlan.userInvoice.paymentStatus.slice(1)}
                  </Typography>
                </Stack>
                {!isNextPlanPaid ? (
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      borderRadius: theme.spacing(2),
                      background: theme.palette.primary.dark
                    }}
                    onClick={() => setOpenModalPlanSummary(true)}
                    // disabled={responseCreatePaymentLinkManually.loading}
                  >
                    {/* {responseCreatePaymentLinkManually.loading
                      ? 'Loading...'
                      : 'Pay Now'} */}
                    Pay Now
                  </Button>
                ) : null}
              </Box>
              <Box
                sx={{
                  padding: theme.spacing(2),
                  background: '#F4FCFF',
                  borderRadius: '6px'
                }}
              >
                {subscriptionData.billing_getUserSubscription.billableMetricPackage.map(
                  (metric) =>
                    metric.quota <= 0 ? null : (
                      <Stack
                        direction="row"
                        sx={{ margin: theme.spacing(1, 0) }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            lineHeight: '22px',
                            color: theme.palette.primary.dark
                          }}
                        >
                          {metric.quota}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 400,
                            lineHeight: '22px',
                            color: theme.palette.grey[500],
                            marginLeft: theme.spacing(1)
                          }}
                        >
                          {metric.name}
                        </Typography>
                      </Stack>
                    )
                )}
              </Box>
            </Stack>
          </Item>
          {/* {isFreeTrial && !isNextPlanPaid ? ( */}
          <Stack
            direction="row"
            spacing={3}
            justifyContent="center"
            sx={{ padding: theme.spacing(2) }}
          >
            <Button
              variant="contained"
              sx={{
                borderRadius: theme.spacing(2),
                background: theme.palette.primary.dark
              }}
              onClick={() => navigate(BILLING_CHOOSE_YOUR_PLAN)}
            >
              Upgrade Plan
            </Button>
          </Stack>
          {/* ) : null} */}
        </>
      )}
      {openModalPlanSummary ? (
        <ModalPlanSummary
          open={openModalPlanSummary}
          handleClose={() => setOpenModalPlanSummary(false)}
          // onDone={handleClickCreatePaymentLinkManually}
        />
      ) : null}
    </Container>
  );
};

export default CurrentPlan;
